import { Container, Sprite, Text } from 'pixi.js'
import gsap from 'gsap'
import { delay } from '../../framework/core/async'
import { layoutMetrics, type Orientation, type Resizable } from '../../framework/render/design'
import { uiTexture } from '../assets/UiTextures'
import { WHEEL_INNER_MULTIPLIER_ARRAY } from '../constants'
import type { SlotResultView } from '../model/SlotResult'
import type { ReelsView } from '../view/ReelsView'

// 左側常駐 Bonus 立輪 — 沿用 Unity pic_wheel_bg4（轉盤）/ bg2（魚框指針）/ bg1（中心軸）。
// 平時壓在主盤框後（z 較低）只從框左緣露出一小條；觸發 Bonus 時彈出至前景、旋轉、再收回。
// 佈局以「圓心」為本地原點（0,0），藏／彈動畫只移動整個容器 position 與 scale，內部不需重排。

// 藏匿時圓心深入主盤框內的比例（佔半徑）；露出框外的左側為 (1−此值)×半徑 寬。
const PEEK_RATIO = 0.45
// 彈出至畫面中央演出時的放大倍率（置中本身已夠大，靠位移 back.out 做彈跳）。
const POP_SCALE = 1.0

// wheel_disc 的 8 扇形中央落在 pixi 角度 k×45°（0/45/…315，正上下左右）；指針固定在 12 點（270°）。
const SECTOR_COUNT = WHEEL_INNER_MULTIPLIER_ARRAY.length
const SECTOR_STEP = (Math.PI * 2) / SECTOR_COUNT
const POINTER_ANGLE = Math.PI * 1.5 // 12 點（pixi：x=cos,y=sin,y 向下 → (0,−1) 為上方）
// 數字標籤放置半徑（佔半徑比例，落在扇形彩色中段）與字級比例。
const LABEL_RADIUS_RATIO = 0.44
const LABEL_FONT_RATIO = 0.12
// 魚框指針：寬度佔轉盤直徑比例、V 尖落點（佔半徑比例）；越小指針越往下、越貼合轉盤外緣。
// 0.15 讓魚框 V 尖貼齊轉盤頂端外緣、框住頂部中獎扇形（對齊真實遊戲）。
const POINTER_WIDTH_RATIO = 0.34
const POINTER_TIP_RATIO = 0.15
// 常駐（盤面背後）時轉盤緩慢自轉一圈所需秒數。
const IDLE_SPIN_SECONDS = 10

export class SideWheelView extends Container implements Resizable {
  private readonly reels: ReelsView
  private readonly frame = new Sprite(uiTexture('wheel_frame'))
  private readonly disc = new Container()
  private readonly discSprite = new Sprite(uiTexture('wheel_disc'))
  private readonly hub = new Sprite(uiTexture('wheel_hub'))
  private readonly labels: Text[] = []
  private radius = 160
  private hiddenX = 0
  private shownX = 0
  private shownY = 0
  private centerY = 0
  private popped = false
  private idleTween: gsap.core.Tween | null = null

  constructor(reels: ReelsView) {
    super()
    this.reels = reels
    this.discSprite.anchor.set(0.5)
    this.disc.addChild(this.discSprite)

    for (let i = 0; i < SECTOR_COUNT; i++) {
      const label = new Text({
        text: `${WHEEL_INNER_MULTIPLIER_ARRAY[i]}`,
        style: {
          fill: 0x5a2400,
          fontWeight: 'bold',
          fontFamily: 'Arial',
          fontSize: 26,
          stroke: { color: 0xffffff, width: 4 },
        },
      })
      label.anchor.set(0.5)
      this.labels.push(label)
      this.disc.addChild(label)
    }

    this.hub.anchor.set(0.5)
    this.frame.anchor.set(0.5, 1) // 底部中心 = V 尖，方便對準轉盤頂端
    // 轉盤（含標籤，會旋轉）→ 中心軸 → 魚框指針（固定不轉，疊最上）
    this.addChild(this.disc, this.hub, this.frame)
    this.startIdleSpin() // 常駐狀態即緩慢自轉
  }

  resize(width: number, height: number, orientation: Orientation): void {
    const metrics = layoutMetrics(orientation)
    const usableTop = height * metrics.topReserve
    const usableH = height * (1 - metrics.barHeight) - usableTop
    const rect = this.reels.frameRect
    const frameTop = rect.height > 0 ? rect.cy - rect.height / 2 : usableTop + usableH * 0.2
    const frameLeft = rect.width > 0 ? rect.cx - rect.width / 2 : width * 0.2
    // 直版：轉盤置於主盤框「上方頂部中央」露出（對齊截圖2）；橫版：大轉盤壓在框左緣露出
    const diameter =
      orientation === 'portrait'
        ? Math.min(width * 0.42, Math.max(frameTop, 1) * 1.5)
        : Math.min(usableH * 0.74, width * 0.4)
    this.radius = diameter / 2

    const discTex = this.discSprite.texture
    if (discTex.width > 0) this.discSprite.scale.set(diameter / discTex.width)
    const hubTex = this.hub.texture
    if (hubTex.width > 0) this.hub.scale.set((diameter * 0.2) / hubTex.width)

    // 內部一律以圓心為本地原點 (0,0) 佈局：藏／彈靠整個容器位移即可。
    this.disc.position.set(0, 0)
    this.hub.position.set(0, 0)

    // 魚框指針：固定在轉盤頂端（12 點），V 尖對準扇形外緣
    const frameTex = this.frame.texture
    if (frameTex.width > 0) {
      this.frame.scale.set((diameter * POINTER_WIDTH_RATIO) / frameTex.width)
      this.frame.position.set(0, -this.radius * POINTER_TIP_RATIO)
    }

    // 倍數標籤放在各扇形中央（角度 i×45°），隨轉盤旋轉
    const labelR = this.radius * LABEL_RADIUS_RATIO
    for (let i = 0; i < this.labels.length; i++) {
      const angle = i * SECTOR_STEP
      this.labels[i].position.set(Math.cos(angle) * labelR, Math.sin(angle) * labelR)
      this.labels[i].style.fontSize = Math.max(14, this.radius * LABEL_FONT_RATIO)
    }

    // 常駐錨點：直版＝主盤框上方頂部中央（露出下半於框上緣）；橫版＝壓在框左緣露出一條
    if (orientation === 'portrait') {
      this.hiddenX = width / 2
      this.centerY = frameTop - this.radius * 0.25
    } else {
      this.hiddenX = frameLeft + this.radius * PEEK_RATIO
      this.centerY = rect.height > 0 ? rect.cy : usableTop + usableH / 2
    }
    // 彈出：移到畫面正中央演出（垂直略偏上，留出頂端魚框指針空間）
    this.shownX = width / 2
    this.shownY = usableTop + usableH * 0.5

    // 套用目前狀態（避免 bonus 中途 resize 跳位）
    if (this.popped) this.position.set(this.shownX, this.shownY)
    else this.position.set(this.hiddenX, this.centerY)
  }

  // 觸發 Bonus：停止常駐自轉 → 彈出 → 旋轉停在 bgInner 結果格（對齊頂端指針）→ 收回 → 恢復自轉。
  async play(view: SlotResultView): Promise<void> {
    this.idleTween?.kill()
    this.idleTween = null
    await this.popOut()

    const twoPi = Math.PI * 2
    // 自轉會累積 rotation，先正規化到 [0,2π)，再自當前角度往正向多轉 turns 圈後對齊結果格
    this.disc.rotation = ((this.disc.rotation % twoPi) + twoPi) % twoPi
    const index = ((view.result.bgResult.bgInner % SECTOR_COUNT) + SECTOR_COUNT) % SECTOR_COUNT
    const turns = 4
    const align = (((POINTER_ANGLE - index * SECTOR_STEP) % twoPi) + twoPi) % twoPi
    const forward = (((align - this.disc.rotation) % twoPi) + twoPi) % twoPi
    const target = this.disc.rotation + twoPi * turns + forward

    await new Promise<void>((resolve) => {
      gsap.to(this.disc, {
        rotation: target,
        duration: 2.6,
        ease: 'power3.out',
        onComplete: () => resolve(),
      })
    })
    this.disc.rotation = align
    await delay(700)

    await this.popIn()
    this.startIdleSpin() // 收回後恢復常駐緩轉
  }

  // 常駐自轉：等速緩慢旋轉一圈（2π），無縫循環（結尾角度等同起始）。
  private startIdleSpin(): void {
    this.idleTween?.kill()
    this.idleTween = gsap.to(this.disc, {
      rotation: `+=${Math.PI * 2}`,
      duration: IDLE_SPIN_SECONDS,
      ease: 'none',
      repeat: -1,
    })
  }

  // 彈出：提至最上層蓋過主盤，往框外滑出並放大彈跳。
  private async popOut(): Promise<void> {
    this.popped = true
    const parent = this.parent
    if (parent !== null) parent.setChildIndex(this, parent.children.length - 1)
    await new Promise<void>((resolve) => {
      gsap
        .timeline({ onComplete: () => resolve() })
        .to(this.position, { x: this.shownX, y: this.shownY, duration: 0.5, ease: 'back.out(1.4)' }, 0)
        .fromTo(
          this.scale,
          { x: 1, y: 1 },
          { x: POP_SCALE, y: POP_SCALE, duration: 0.5, ease: 'back.out(1.4)' },
          0,
        )
    })
  }

  // 收回：縮回原尺寸、滑回藏匿位置，再把層級還原到主盤之下。
  private async popIn(): Promise<void> {
    await new Promise<void>((resolve) => {
      gsap
        .timeline({ onComplete: () => resolve() })
        .to(this.position, { x: this.hiddenX, y: this.centerY, duration: 0.35, ease: 'power2.in' }, 0)
        .to(this.scale, { x: 1, y: 1, duration: 0.35, ease: 'power2.in' }, 0)
    })
    this.popped = false
    const parent = this.parent
    if (parent !== null) parent.setChildIndex(this, parent.getChildIndex(this.reels))
  }
}
